import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Notice, Modal, inputCls } from "@/dashboard/ui";
import { Facebook, Instagram, Link2, Unlink, RefreshCw } from "lucide-react";

const STATUS_PILL = { connected: "green", reconnect_required: "amber", error: "red" };
const STATUS_LABEL = { connected: "Connected", reconnect_required: "Reconnection required", error: "Connection error" };

export default function SocialAccountsPanel() {
  const [status, setStatus] = useState(null);
  const [flow, setFlow] = useState(null);       // {flowId, pages} selection step
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/social/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    const meta = params.get("meta");
    const flowId = params.get("flow");
    if (meta === "denied") toast.error("Authorisation was declined — nothing was connected.");
    if (meta === "expired" || meta === "invalid") toast.error("That connection attempt expired — please start again.");
    if (meta === "no-pages") toast.error("No manageable Facebook Pages were found on that Meta account.");
    if (meta === "select" && flowId) {
      // the backend stashed the discovered pages; fetch them via the selection session
      setFlow({ flowId });
      api.get("/social/status").then(() => {});
      // pages are fetched from a dedicated endpoint below
      api.post("/social/connect/pages", { flow_id: flowId })
        .then(({ data }) => setPages(data.pages))
        .catch((e) => toast.error(formatApiError(e)));
    }
    if (meta) { params.delete("meta"); params.delete("flow"); setParams(params, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/social/connect/start");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const mockConnect = async () => {
    setBusy(true);
    try {
      await api.post("/social/mock/connect");
      toast.success("DEMO connection created — clearly labelled, cannot publish");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selectPage = async () => {
    setBusy(true);
    try {
      await api.post("/social/connect/select", { flow_id: flow.flowId, page_id: selectedPage, connect_instagram: true });
      toast.success("Account connected — nothing will be published automatically");
      setFlow(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (id) => {
    try {
      const { data } = await api.post(`/social/connections/${id}/verify`);
      toast.success(data.status === "connected" ? "Connection verified with Meta" : "Reconnection required");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/social/connections/${confirming.id}`);
      toast.success(data.meta_revoked === false
        ? "Disconnected locally — Meta did not confirm revocation"
        : "Disconnected. Your content plans and calendar are untouched.");
      setConfirming(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <p className="text-sm text-neutral-400">Loading…</p>;
  const conns = status.connections || [];
  const byPlatform = (p) => conns.find((c) => c.platform === p);

  const ConnectionCard = ({ platform, icon: Icon, label }) => {
    const c = byPlatform(platform);
    return (
      <Card className="p-5" data-testid={`social-${platform}-card`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Icon size={16} className="text-[#111111]" />
            <p className="text-sm font-bold text-[#111111]">{label}</p>
          </div>
          {c ? (
            <Pill tone={STATUS_PILL[c.status] || "grey"}>{STATUS_LABEL[c.status] || c.status}</Pill>
          ) : (
            <Pill tone="grey">Not connected</Pill>
          )}
        </div>
        {c ? (
          <div className="mt-3 text-xs text-neutral-500" data-testid={`social-${platform}-info`}>
            <p className="font-semibold text-neutral-700">
              {c.display_name}
              {c.mock && <span className="ml-2 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Demo</span>}
            </p>
            <p className="mt-1">Connected {String(c.connected_at || "").slice(0, 10)} · Permissions: {(c.permissions || []).join(", ") || "—"}</p>
            {c.last_verified_at && <p className="mt-0.5">Last verified {String(c.last_verified_at).slice(0, 16).replace("T", " ")}</p>}
            {platform === "facebook" && !c.mock && c.status === "connected" && (
              <p className={`mt-1 font-medium ${c.can_publish ? "text-green-700" : "text-amber-700"}`} data-testid="social-facebook-publish-status">
                {c.can_publish
                  ? "Publishing access granted — posts still go out only when you click Publish now on an approved post."
                  : "Publishing access not granted yet — reconnect to allow manual publishing of approved posts."}
              </p>
            )}
          </div>
        ) : (
          <p className="mt-3 text-xs text-neutral-500">
            {platform === "facebook" ? "Connect a Facebook Page you manage." : "Connect an Instagram professional (Business or Creator) account linked to your Page. Personal accounts aren't supported."}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {c?.status === "reconnect_required" && (
            <Btn data-testid={`social-${platform}-reconnect`} disabled={busy || !status.configured} onClick={startConnect}>
              <RefreshCw size={13} className="mr-1 inline" /> Reconnect
            </Btn>
          )}
          {platform === "facebook" && c?.status === "connected" && !c.mock && !c.can_publish && (
            <Btn data-testid="social-facebook-reconnect-publish" disabled={busy || !status.configured} onClick={startConnect}>
              <RefreshCw size={13} className="mr-1 inline" /> Reconnect to grant publishing access
            </Btn>
          )}
          {c && (
            <>
              <Btn variant="ghost" data-testid={`social-${platform}-verify`} onClick={() => verify(c.id)}>Check status</Btn>
              <Btn variant="ghost" data-testid={`social-${platform}-disconnect`} onClick={() => setConfirming(c)}>
                <Unlink size={13} className="mr-1 inline" /> Disconnect
              </Btn>
            </>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-5" data-testid="social-accounts-panel">
      <Notice testid="social-notice" title="Social account connections are being prepared"
        body="Connecting an account does not automatically publish any content. Approving a post in your content plan never publishes it externally." />
      {!status.configured && (
        <Notice testid="social-setup-required" title="Setup required"
          body="Meta app credentials are not configured yet, so live connections are disabled. Your dashboard administrator needs to add the Meta App ID, App Secret and redirect URI before accounts can connect." />
      )}
      {status.configured && !status.instagram_available && (
        <Notice testid="social-instagram-setup" title="Instagram connection needs extra setup"
          body="Facebook Pages can be connected now. Instagram professional accounts additionally require the Meta app's Instagram use case to be configured — the connection button stays Facebook-only until then. Nothing is claimed to work until it does." />
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <ConnectionCard platform="facebook" icon={Facebook} label="Facebook Page" />
        <ConnectionCard platform="instagram" icon={Instagram} label="Instagram professional account" />
      </div>
      <div className="flex items-center gap-3">
        <Btn data-testid="social-connect-button" disabled={busy || !status.configured} onClick={startConnect}>
          <Link2 size={14} className="mr-1.5 inline" />
          {status.configured ? "Connect with Meta" : "Connect with Meta (setup required)"}
        </Btn>
      </div>

      {flow && (
        <Modal title="Choose your Facebook Page" onClose={() => setFlow(null)} testid="social-select-modal">
          {pages.length === 0 ? (
            <p className="text-sm text-neutral-500">Loading pages…</p>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-neutral-500">Select the Page this business manages. An Instagram professional account linked to the Page will be offered too.</p>
              <div className="space-y-1.5" data-testid="social-page-list">
                {pages.map((p) => (
                  <label key={p.page_id} className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-sm ${selectedPage === p.page_id ? "border-[#111111] bg-neutral-50" : "border-neutral-200"}`}>
                    <span>
                      <span className="font-semibold">{p.page_name}</span>
                      {p.instagram_id
                        ? <span className="ml-2 text-xs text-green-700">Instagram professional account linked</span>
                        : <span className="ml-2 text-xs text-neutral-400">No linked Instagram professional account</span>}
                    </span>
                    <input type="radio" name="page" className="accent-neutral-900" data-testid={`social-page-${p.page_id}`}
                      checked={selectedPage === p.page_id} onChange={() => setSelectedPage(p.page_id)} />
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => setFlow(null)}>Cancel</Btn>
                <Btn data-testid="social-select-confirm" disabled={!selectedPage || busy} onClick={selectPage}>
                  {busy ? "Connecting…" : "Connect selected Page"}
                </Btn>
              </div>
            </div>
          )}
        </Modal>
      )}

      {confirming && (
        <Modal title="Disconnect account" onClose={() => setConfirming(null)} testid="social-disconnect-modal">
          <p className="text-sm text-neutral-600">
            Disconnect <span className="font-semibold">{confirming.display_name}</span>? Stored access credentials will be removed and Meta authorisation revoked where Meta confirms it. Your content plans, drafts and calendar entries stay untouched.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Btn variant="ghost" onClick={() => setConfirming(null)}>Keep connected</Btn>
            <Btn data-testid="social-disconnect-confirm" disabled={busy} onClick={disconnect}>{busy ? "Disconnecting…" : "Disconnect"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
